import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LimitationsForm from '../../components/LimitationsForm';
import { getDefaultExpirationDate, getDefaultLimitations } from '../../helpers/companyDefaults';
import { Organization, OrganizationLimitation } from '../../services/organization';

interface EditCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  organization: Organization | null;
  onSubmit: (limitations: Partial<OrganizationLimitation>) => void;
}

const EditCompanyModal: React.FC<EditCompanyModalProps> = ({
  isOpen,
  onClose,
  organization,
  onSubmit,
}) => {
  // All hooks at the top
  const { t } = useTranslation();

  const [formData, setFormData] = useState<Partial<OrganizationLimitation>>({
    storage_gb: 0,
    question: 0,
    translation: 0,
    expiration: '',
  });

  const [unlimitedFlags, setUnlimitedFlags] = useState({
    question: false,
    storage: false,
    translation: false,
    expiration: false,
  });

  const [originalValues, setOriginalValues] = useState<Partial<OrganizationLimitation>>({});

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Non-hook variables — DS tokens (design-guidelines.md)
  const inputBg = 'bg.canvas';
  const inputBorder = 'border.default';

  useEffect(() => {
    if (organization && organization.limitation) {
      const isExpirationUnlimitedFromBackend =
        organization.limitation.expiration === 'Unlimited' ||
        organization.limitation.expiration === null ||
        organization.limitation.expiration === undefined;

      const initialExpirationFormData = isExpirationUnlimitedFromBackend
        ? null // If unlimited from backend, formData.expiration should be null
        : organization.limitation.expiration
          ? new Date(organization.limitation.expiration).toISOString().split('T')[0] // Otherwise, parse the date
          : null; // Fallback to null if expiration is null/undefined

      // Store original values for restoration - keep null values as null
      setOriginalValues({
        storage_gb: organization.limitation.storage_gb,
        question: organization.limitation.question,
        translation: organization.limitation.translation,
        expiration: organization.limitation.expiration,
      });

      setFormData({
        storage_gb: organization.limitation.storage_gb, // Keep null as null
        question: organization.limitation.question, // Keep null as null
        translation: organization.limitation.translation, // Keep null as null
        expiration: initialExpirationFormData, // Use the correctly determined value
      });

      // Set unlimited flags based on current values
      setUnlimitedFlags({
        question:
          organization.limitation.question === null ||
          organization.limitation.question === undefined,
        storage:
          organization.limitation.storage_gb === null ||
          organization.limitation.storage_gb === undefined,
        translation:
          organization.limitation.translation === null ||
          organization.limitation.translation === undefined,
        expiration: isExpirationUnlimitedFromBackend, // Use the same logic for the flag
      });
    }
  }, [organization]);

  const handleInputChange = (field: string, value: string | number) => {
    const fieldKey = field.replace('limitation.', '') as keyof OrganizationLimitation;
    setFormData((prev: Partial<OrganizationLimitation>) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const handleUnlimitedToggle = (field: 'question' | 'storage' | 'translation' | 'expiration') => {
    const currentValue = unlimitedFlags[field];
    const newValue = !currentValue;

    console.log('🔄 Toggle field:', field, 'from', currentValue, 'to', newValue);
    console.log('📊 Original values:', originalValues);
    console.log('📋 Current form data:', formData);

    // Set unlimited flags first
    setUnlimitedFlags((prev) => ({
      ...prev,
      [field]: newValue,
    }));

    // Then set form data based on the new value
    if (field === 'expiration') {
      let newExpirationValue;

      if (currentValue) {
        // Chuyển từ "Unlimited" sang "Limited" - restore original value hoặc default 1 năm
        if (originalValues.expiration && originalValues.expiration !== 'Unlimited') {
          // Restore original date value
          newExpirationValue = new Date(originalValues.expiration).toISOString().split('T')[0];
        } else {
          // No original value, use default 1 year
          newExpirationValue = getDefaultExpirationDate().toISOString().split('T')[0];
        }
      } else {
        // Chuyển từ "Limited" sang "Unlimited" - set null
        newExpirationValue = null;
      }

      setFormData((prev: Partial<OrganizationLimitation>) => {
        const newFormData = {
          ...prev,
          expiration: newExpirationValue,
        };
        console.log('📅 Updated expiration form data:', newFormData);
        return newFormData;
      });
    } else {
      const fieldKey = field === 'storage' ? 'storage_gb' : field;
      let defaultValue;

      // Get default value based on field type
      switch (field) {
        case 'question':
          defaultValue = getDefaultLimitations().question;
          break;
        case 'translation':
          defaultValue = getDefaultLimitations().translation;
          break;
        case 'storage':
          defaultValue = getDefaultLimitations().storage_gb;
          break;
        default:
          defaultValue = 0;
      }

      // If switching to limited (newValue = false), use default value
      // If switching to unlimited (newValue = true), use null
      const newFieldValue = newValue ? null : defaultValue;
      console.log(
        '🔧 Field:',
        fieldKey,
        'New value:',
        newFieldValue,
        'Original:',
        originalValues[fieldKey],
        'Default:',
        defaultValue
      );

      setFormData((prev: Partial<OrganizationLimitation>) => {
        // Only update the specific field that was toggled
        const newFormData = {
          ...prev,
          [fieldKey]: newFieldValue,
        };
        console.log('📝 Updated form data:', newFormData);
        return newFormData;
      });
    }
  };

  const handleSubmit = () => {
    if (!organization) return;

    setIsSubmitting(true);

    // Convert storage_gb from GB to bytes before sending to API
    const apiPayload = {
      ...formData,
      storage_gb: unlimitedFlags.storage ? null : formData.storage_gb || 0,
    };

    // Call parent's onSubmit to handle API call
    onSubmit(apiPayload);
    onClose();
    setIsSubmitting(false);
  };

  const handleClose = () => {
    setFormData({
      storage_gb: 0,
      question: 0,
      translation: 0,
      expiration: '',
    });
    onClose();
  };

  if (!organization) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='lg'>
      <ModalOverlay bg='rgba(16, 18, 24, 0.45)' backdropFilter='blur(3px)' />
      <ModalContent bg='bg.surfaceRaised' borderRadius='xl' shadow='ds_lg'>
        <ModalHeader
          color='text.default'
          fontFamily='heading'
          fontSize='lg'
          fontWeight={600}
          borderColor='border.default'
          borderBottomWidth='1px'
        >
          {t('managementSystem.editCompany.title')}
        </ModalHeader>
        <ModalCloseButton color='text.muted' />
        <ModalBody>
          <VStack align='stretch' spacing={4}>
            <FormControl>
              <FormLabel color='text.muted' fontSize='sm' fontWeight={500}>
                {t('managementSystem.editCompany.company')}
              </FormLabel>
              <Input
                color='text.default'
                bg={inputBg}
                borderColor={inputBorder}
                _hover={{ cursor: 'not-allowed' }}
                cursor='not-allowed'
                isDisabled
                isReadOnly
                value={organization.name}
              />
            </FormControl>

            <LimitationsForm
              limitations={formData}
              unlimitedFlags={unlimitedFlags}
              onToggleUnlimited={handleUnlimitedToggle}
              onInputChange={handleInputChange}
              isEdit={true}
            />
          </VStack>
        </ModalBody>

        <ModalFooter gap={3} borderColor='border.default' borderTopWidth='1px'>
          <Button onClick={handleClose} size='ds-md' variant='ds-ghost'>
            {t('managementSystem.editCompany.cancel')}
          </Button>
          <Button
            isLoading={isSubmitting}
            loadingText='Saving...'
            onClick={handleSubmit}
            size='ds-md'
            variant='ds-primary'
          >
            {t('managementSystem.editCompany.saveChanges')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditCompanyModal;
